// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState } from 'expo-router';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { computeStrokesReceivedForHole, parseHandicapValue } from '@/features/hole/utils';
import { useUser } from '../../user-context';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildAccessHeaders, appendClaimQuery, hasClaimAccess } from '@/services/claimHttp';
import { getActiveClaimSessionSync, loadClaimSession, subscribeClaimSession } from '@/services/claimSession';
import { useRoundState } from '@/stores/roundState';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';

type Player = {
  id: string;
  name: string;
  handicap?: number | null;
  Handicap?: number | null;
  hcp?: number | null;
  Hcp?: number | null;
  playingHandicap?: number | null;
  playing_handicap?: number | null;
};

type HoleScore = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  hole?: number;
  Hole?: number;
  par?: number;
  Par?: number;
  holePar?: number;
  HolePar?: number;
  strokeIndex?: number;
  StrokeIndex?: number;
  holeIndex?: number;
  HoleIndex?: number;
  grossStrokes?: number | null;
  GrossStrokes?: number | null;
  grossStroke?: number | null;
  GrossStroke?: number | null;
  handicapStrokes?: number | null;
  HandicapStroke?: number | null;
  handicapStroke?: number | null;
  pickup?: boolean;
  Pickup?: boolean;
};

type RoundInfo = {
  courseName?: string;
  courseAddress?: string;
  name?: string;
  description?: string;
  courseId?: string;
  CourseId?: string;
};

type HoleMetaCell = {
  par?: number;
  strokeIndex?: number;
};

type RoundSummary = {
  id: string;
  courseName?: string;
  name?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  statusUpdatedAt?: string;
};

type ScoreboardScore = {
  gameId?: string;
  GameId?: string;
  gameName?: string;
  GameName?: string;
  playerId?: string;
  PlayerId?: string;
  scorePoints?: number;
  ScorePoints?: number;
};

type ScoreboardResponse = {
  scores?: ScoreboardScore[];
  Scores?: ScoreboardScore[];
};

type ScoreHistoryRow = {
  playerId?: string;
  PlayerId?: string;
  gameId?: string;
  GameId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  scorePoints?: number;
  ScorePoints?: number;
};

type GameDefinition = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
};

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);
const COL_WIDTH = 44;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const extractHoleMetaEntry = (entry: Record<string, unknown>): { hole: number; meta: HoleMetaCell } | null => {
  const holeCandidate =
    toFiniteNumber(entry.holeNumber) ??
    toFiniteNumber(entry.HoleNumber) ??
    toFiniteNumber(entry.hole) ??
    toFiniteNumber(entry.Hole) ??
    toFiniteNumber(entry.number) ??
    toFiniteNumber(entry.Number);
  const hole = holeCandidate ? Math.trunc(holeCandidate) : 0;
  if (!hole || hole < 1 || hole > 18) return null;

  const par =
    toFiniteNumber(entry.par) ??
    toFiniteNumber(entry.Par) ??
    toFiniteNumber(entry.holePar) ??
    toFiniteNumber(entry.HolePar);
  const strokeIndex =
    toFiniteNumber(entry.strokeIndex) ??
    toFiniteNumber(entry.StrokeIndex) ??
    toFiniteNumber(entry.holeIndex) ??
    toFiniteNumber(entry.HoleIndex) ??
    toFiniteNumber(entry.index) ??
    toFiniteNumber(entry.Index);

  if (par == null && strokeIndex == null) return null;
  return {
    hole,
    meta: {
      par: par == null ? undefined : Math.trunc(par),
      strokeIndex: strokeIndex == null ? undefined : Math.trunc(strokeIndex),
    },
  };
};

const extractHoleMetaFromRoundPayload = (payload: unknown): Map<number, HoleMetaCell> => {
  const meta = new Map<number, HoleMetaCell>();
  if (!payload || typeof payload !== 'object') return meta;
  const obj = payload as Record<string, unknown>;

  const applyEntry = (entry: Record<string, unknown>) => {
    const normalized = extractHoleMetaEntry(entry);
    if (!normalized) return;
    const existing = meta.get(normalized.hole) ?? {};
    meta.set(normalized.hole, {
      par: existing.par ?? normalized.meta.par,
      strokeIndex: existing.strokeIndex ?? normalized.meta.strokeIndex,
    });
  };

  const applySource = (source: unknown) => {
    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (item && typeof item === 'object') {
          applyEntry(item as Record<string, unknown>);
        }
      });
      return;
    }
    if (source && typeof source === 'object') {
      Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
        if (!value || typeof value !== 'object') return;
        const item = value as Record<string, unknown>;
        applyEntry({ holeNumber: key, ...item });
      });
    }
  };

  applySource(obj.holes);
  applySource(obj.Holes);
  applySource(obj.holeInfos);
  applySource(obj.HoleInfos);
  applySource(obj.holeMeta);
  applySource(obj.HoleMeta);
  if (obj.course && typeof obj.course === 'object') {
    const course = obj.course as Record<string, unknown>;
    applySource(course.holes);
    applySource(course.Holes);
    applySource(course.holeInfos);
    applySource(course.HoleInfos);
    applySource(course.holeMeta);
    applySource(course.HoleMeta);
  }
  if (obj.Course && typeof obj.Course === 'object') {
    const course = obj.Course as Record<string, unknown>;
    applySource(course.holes);
    applySource(course.Holes);
    applySource(course.holeInfos);
    applySource(course.HoleInfos);
    applySource(course.holeMeta);
    applySource(course.HoleMeta);
  }

  return meta;
};

const mergeHoleMeta = (primary: Map<number, HoleMetaCell>, fallback: Map<number, HoleMetaCell>): Map<number, HoleMetaCell> => {
  const merged = new Map<number, HoleMetaCell>();
  HOLES.forEach((hole) => {
    const a = primary.get(hole);
    const b = fallback.get(hole);
    if (!a && !b) return;
    merged.set(hole, {
      par: a?.par ?? b?.par,
      strokeIndex: a?.strokeIndex ?? b?.strokeIndex,
    });
  });
  return merged;
};

const isRoundSelectableByStatus = (status: string | undefined): boolean => {
  const normalized = String(status ?? '').trim().toLowerCase();
  return (
    normalized === 'open' ||
    normalized === 'active' ||
    normalized === 'playing' ||
    normalized === 'started' ||
    normalized === 'finished'
  );
};

export default function ScorecardScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { tourId: tourParam, roundId: roundIdParam } = useLocalSearchParams<{ tourId?: string; roundId?: string }>();
  const roundId = useRoundState((state) => state.roundId);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setRoundStatus = useRoundState((state) => state.setRoundStatus);
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const [tourId, setTourId] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<HoleScore[]>([]);
  const [scoreboardScores, setScoreboardScores] = useState<ScoreboardScore[]>([]);
  const [scoreHistoryRows, setScoreHistoryRows] = useState<ScoreHistoryRow[]>([]);
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [roundGameDefinitionIds, setRoundGameDefinitionIds] = useState<string[]>([]);
  const [roundInfo, setRoundInfo] = useState<RoundInfo | null>(null);
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsError, setRoundsError] = useState<string | null>(null);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const [roundHoleMeta, setRoundHoleMeta] = useState<Map<number, HoleMetaCell>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasGuestClaimSessionAccess = !!claimSession?.claimClientId && (
    hasClaimAccess(claimSession) ||
    !!claimSession?.inviteToken?.trim()
  );
  const isGuestClaimMode = !user?.token && hasGuestClaimSessionAccess;
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'scorecard',
      }),
    [apiBaseUrl, user?.token]
  );

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isNavigationReady) return;
    const resolvedTour = tourParam ?? getCurrentTourId() ?? claimSession?.tourId ?? null;
    setTourId(resolvedTour ?? null);
    if (resolvedTour) {
      setCurrentTourId(resolvedTour);
    }
  }, [claimSession?.tourId, isNavigationReady, tourParam]);

  useEffect(() => {
    const resolvedRound =
      (Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam) ??
      claimSession?.startedRoundId ??
      null;
    if (resolvedRound && resolvedRound !== roundId) {
      setRoundId(resolvedRound);
    }
  }, [claimSession?.startedRoundId, roundId, roundIdParam, setRoundId]);

  useEffect(() => {
    const selectedRound = rounds.find((candidate) => candidate.id === roundId) ?? null;
    setRoundStatus(selectedRound?.status ?? null);
  }, [roundId, rounds, setRoundStatus]);

  useEffect(() => {
    if (!tourId || !isNavigationReady || (!user?.token && !isGuestClaimMode)) return;
    const loadRounds = async () => {
      setRoundsLoading(true);
      setRoundsError(null);
      try {
        const response = await fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/tour/${tourId}`, claimSession), {
          headers: buildAccessHeaders(user?.token),
        });
        if (!response.ok) {
          throw new Error(`${t('scorecard.selectRoundError')} (${response.status})`);
        }
        const payload = (await response.json()) as RoundSummary[];
        setRounds(payload);
      } catch (err) {
        setRoundsError(t('scorecard.selectRoundError'));
        logToBff('scorecard.rounds.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setRoundsLoading(false);
      }
    };
    loadRounds();
  }, [apiBaseUrl, claimSession, isGuestClaimMode, isNavigationReady, logToBff, t, tourId, user?.token]);

  useEffect(() => {
    if (!roundId || !isNavigationReady || (!user?.token && !isGuestClaimMode)) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = buildAccessHeaders(user?.token);
        const [playersRes, scoresRes, roundRes, scoreboardRes, scoreHistoryRes, gameDefsRes] = await Promise.all([
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/players`, claimSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`, claimSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}`, claimSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/scoreboard`, claimSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/score-history`, claimSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/game-definitions`, claimSession), {
            headers,
          }),
        ]);

        if (!playersRes.ok) {
          throw new Error(`${t('scorecard.loadError')} (${playersRes.status})`);
        }
        if (!scoresRes.ok) {
          throw new Error(`${t('scorecard.loadError')} (${scoresRes.status})`);
        }

        const [playerPayload, scorePayload, roundPayload, scoreboardPayload, scoreHistoryPayload, gameDefsPayload] = await Promise.all([
          playersRes.json(),
          scoresRes.json(),
          roundRes.ok ? roundRes.json() : Promise.resolve(null),
          scoreboardRes.ok ? scoreboardRes.json() : Promise.resolve({ scores: [] }),
          scoreHistoryRes.ok ? scoreHistoryRes.json() : Promise.resolve([]),
          gameDefsRes.ok ? gameDefsRes.json() : Promise.resolve([]),
        ]);

        const playerData = playerPayload as { players?: Player[]; Players?: Player[] };
        const scoreboardData = scoreboardPayload as ScoreboardResponse;
        const normalizedScoreboard = Array.isArray(scoreboardData.scores)
          ? scoreboardData.scores
          : Array.isArray(scoreboardData.Scores)
            ? scoreboardData.Scores
            : [];
        const roundData = roundPayload as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        } | null;
        const resolvedRoundGameDefinitionIds = Array.isArray(roundData?.gameDefinitionIds)
          ? roundData?.gameDefinitionIds
          : Array.isArray(roundData?.GameDefinitionIds)
            ? roundData?.GameDefinitionIds
            : [];
        const rawPlayers = playerData.players ?? playerData.Players ?? [];
        const normalizedPlayers = rawPlayers.map((player) => {
          const handicap = parseHandicapValue(
            player.handicap ??
            player.Handicap ??
            player.hcp ??
            player.Hcp ??
            player.playingHandicap ??
            player.playing_handicap
          );
          return { ...player, handicap };
        });
        setPlayers(normalizedPlayers);
        setScores((scorePayload as HoleScore[]) ?? []);
        const normalizedRoundInfo = roundPayload as RoundInfo | null;
        setRoundInfo(normalizedRoundInfo);
        setRoundStatus(((roundPayload as { status?: string } | null)?.status) ?? null);
        const roundMeta = extractHoleMetaFromRoundPayload(roundPayload);
        let resolvedMeta = roundMeta;
        const roundCourseId =
          (normalizedRoundInfo?.courseId ?? normalizedRoundInfo?.CourseId ?? '').toString().trim();
        if (resolvedMeta.size < HOLES.length && roundCourseId) {
          try {
            const courseResponse = await fetch(appendClaimQuery(`${apiBaseUrl}/api/geodata/courses/${roundCourseId}`, claimSession), {
              headers,
            });
            if (courseResponse.ok) {
              const coursePayload = await courseResponse.json();
              const courseMeta = extractHoleMetaFromRoundPayload(coursePayload);
              resolvedMeta = mergeHoleMeta(resolvedMeta, courseMeta);
            }
          } catch {
            // Keep scorecard working with whatever round/hole-entry metadata is available.
          }
        }
        setRoundHoleMeta(resolvedMeta);
        setScoreboardScores(normalizedScoreboard);
        setScoreHistoryRows((scoreHistoryPayload as ScoreHistoryRow[]) ?? []);
        setGameDefinitions((gameDefsPayload as GameDefinition[]) ?? []);
        setRoundGameDefinitionIds(resolvedRoundGameDefinitionIds);
        logToBff('scorecard.loaded', { roundId });
      } catch (err) {
        setError(t('scorecard.loadError'));
        logToBff('scorecard.load.error', { roundId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBaseUrl, claimSession, isGuestClaimMode, isNavigationReady, logToBff, roundId, t, user?.token]);

  const scoreMap = new Map<string, HoleScore>();
  scores.forEach((score) => {
    const playerId = (score.playerId ?? score.PlayerId)?.toString() ?? '';
    const hole = score.holeNumber ?? score.HoleNumber ?? score.hole ?? score.Hole ?? 0;
    if (!playerId || !hole) return;
    scoreMap.set(`${playerId}:${hole}`, score);
  });

  const holeMeta = new Map<number, { par?: number; strokeIndex?: number }>();
  roundHoleMeta.forEach((meta, hole) => {
    holeMeta.set(hole, { par: meta.par, strokeIndex: meta.strokeIndex });
  });
  scores.forEach((score) => {
    const hole = score.holeNumber ?? score.HoleNumber ?? score.hole ?? score.Hole ?? 0;
    if (!hole) return;
    const existing = holeMeta.get(hole) ?? {};
    const fallbackPar =
      score.par ??
      score.Par ??
      score.holePar ??
      score.HolePar;
    const fallbackStrokeIndex =
      score.strokeIndex ??
      score.StrokeIndex ??
      score.holeIndex ??
      score.HoleIndex;
    if (existing.par == null && existing.strokeIndex == null && fallbackPar == null && fallbackStrokeIndex == null) {
      return;
    }
    holeMeta.set(hole, {
      par: existing.par ?? fallbackPar,
      strokeIndex: existing.strokeIndex ?? fallbackStrokeIndex,
    });
  });

  const renderStrokes = (player: Player, hole: number) => {
    const playerId = player.id;
    const score = scoreMap.get(`${playerId}:${hole}`);
    const holeIndex = holeMeta.get(hole)?.strokeIndex ?? null;
    const playerHandicap = parseHandicapValue(
      player.handicap ??
      player.Handicap ??
      player.hcp ??
      player.Hcp ??
      player.playingHandicap ??
      player.playing_handicap
    );
    const fallbackHandicapStrokes = computeStrokesReceivedForHole(playerHandicap ?? null, holeIndex);
    if (!score) {
      if (!fallbackHandicapStrokes || fallbackHandicapStrokes <= 0) return <Text style={styles.strokeValue}>-</Text>;
      return (
        <View style={styles.strokeCell}>
          <Text style={styles.strokeValue}>-</Text>
          <Text style={styles.handicapText}>+{fallbackHandicapStrokes}</Text>
        </View>
      );
    }
    const pickup = score.pickup ?? score.Pickup;
    if (pickup) return <Text style={styles.strokeValue}>X</Text>;
    const strokes = score.grossStrokes ?? score.GrossStrokes ?? score.grossStroke ?? score.GrossStroke;
    const rawHandicap =
      score.handicapStroke ??
      score.HandicapStroke ??
      score.handicapStrokes ??
      score.HandicapStroke ??
      fallbackHandicapStrokes;
    const handicap = typeof rawHandicap === 'number' && Number.isFinite(rawHandicap)
      ? (rawHandicap > 0 ? rawHandicap : fallbackHandicapStrokes)
      : fallbackHandicapStrokes;
    if (strokes == null) return <Text style={styles.strokeValue}>-</Text>;
    if (!handicap || handicap <= 0) return <Text style={styles.strokeValue}>{String(strokes)}</Text>;
    return (
      <View style={styles.strokeCell}>
        <Text style={styles.strokeValue}>{strokes}</Text>
        <Text style={styles.handicapText}>+{handicap}</Text>
      </View>
    );
  };

  const stablefordGameIds = useMemo(() => {
    const ids = new Set<string>();

    scoreboardScores
      .filter((score) => {
        const gameName = (score.gameName ?? score.GameName ?? '').toString().toLowerCase();
        return gameName.includes('stableford');
      })
      .map((score) => (score.gameId ?? score.GameId ?? '').toString())
      .filter((id) => id.length > 0)
      .forEach((id) => ids.add(id));

    const gameDefinitionNameById = new Map<string, string>();
    gameDefinitions.forEach((definition) => {
      const id = (definition.id ?? definition.Id ?? '').toString();
      const name = (definition.name ?? definition.Name ?? '').toString().toLowerCase();
      if (id) {
        gameDefinitionNameById.set(id, name);
      }
    });
    roundGameDefinitionIds.forEach((id) => {
      const name = gameDefinitionNameById.get(id);
      if (name?.includes('stableford')) {
        ids.add(id);
      }
    });

    return ids;
  }, [gameDefinitions, roundGameDefinitionIds, scoreboardScores]);

  // score-history rows carry the per-hole Stableford points for each player/hole, so use them directly.
  const stablefordPointsByPlayerHole = useMemo(() => {
    const map = new Map<string, number>();
    scoreHistoryRows.forEach((row) => {
      const gameId = (row.gameId ?? row.GameId ?? '').toString();
      const playerId = (row.playerId ?? row.PlayerId ?? '').toString();
      const hole = row.holeNumber ?? row.HoleNumber ?? 0;
      const scorePoints = row.scorePoints ?? row.ScorePoints;
      if (!stablefordGameIds.has(gameId) || !playerId || !hole || scorePoints == null) return;
      map.set(`${playerId}:${hole}`, Number(scorePoints));
    });
    return map;
  }, [scoreHistoryRows, stablefordGameIds]);

  const stablefordTotals = useMemo(() => {
    const totals = new Map<string, number>();
    scoreboardScores.forEach((score) => {
      const gameId = (score.gameId ?? score.GameId ?? '').toString();
      const playerId = (score.playerId ?? score.PlayerId ?? '').toString();
      const scorePoints = score.scorePoints ?? score.ScorePoints;
      if (!stablefordGameIds.has(gameId) || !playerId || scorePoints == null) return;
      totals.set(playerId, (totals.get(playerId) ?? 0) + Number(scorePoints));
    });
    return totals;
  }, [scoreboardScores, stablefordGameIds]);

  const renderStableford = (playerId: string, hole: number) => {
    const backendPoints = stablefordPointsByPlayerHole.get(`${playerId}:${hole}`);
    return backendPoints == null ? '-' : backendPoints;
  };

  const grossTotals = new Map<string, number>();
  players.forEach((player) => {
    grossTotals.set(player.id, 0);
  });
  HOLES.forEach((hole) => {
    players.forEach((player) => {
      const score = scoreMap.get(`${player.id}:${hole}`);
      const pickup = score?.pickup ?? score?.Pickup;
      const strokes = score?.grossStrokes ?? score?.GrossStrokes ?? score?.grossStroke ?? score?.GrossStroke;
      if (!pickup && strokes != null) {
        grossTotals.set(player.id, (grossTotals.get(player.id) ?? 0) + strokes);
      }
    });
  });

  const selectedRound = rounds.find((round) => round.id === roundId) ?? null;

  if (!isNavigationReady) return null;
  if (!user && !claimSessionLoaded) return null;
  if (!user && !isGuestClaimMode && !effectiveRoundId) return <Redirect href="/" />;

  if (!roundId) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>{t('scorecard.title')}</Text>
        {tourId ? (
          <>
            <Text style={styles.meta}>{t('scorecard.selectRoundTitle')}</Text>
            {roundsLoading ? <Text style={styles.meta}>{t('scorecard.selectRoundLoading')}</Text> : null}
            {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
            {rounds.length === 0 && !roundsLoading ? <Text style={styles.meta}>{t('scorecard.selectRoundEmpty')}</Text> : null}
            {rounds.map((round) => {
              const isSelectable = isRoundSelectableByStatus(round.status);
              const status = String(round.status ?? '-').trim() || '-';
              return (
                <View key={round.id} style={[styles.roundSelectRow, !isSelectable ? { opacity: 0.5 } : null]}>
                  <Text style={styles.roundSelectName} numberOfLines={1}>
                    {round.name ?? round.courseName ?? t('scorecard.selectRoundFallback')}
                  </Text>
                  <Text style={styles.roundSelectMeta}>
                    {isSelectable ? status : `${status} (Not started)`}
                  </Text>
                  {isSelectable ? (
                    <Text style={styles.roundSelectLink} onPress={() => setRoundId(round.id)}>
                      {t('scorecard.selectRound')}
                    </Text>
                  ) : null}
                </View>
              );
            })}
          </>
        ) : (
          <Text style={styles.meta}>{t('scorecard.missingRound')}</Text>
        )}
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.title}>{t('scorecard.title')}</Text>
      {tourId && rounds.length ? (
        <>
          <Text style={styles.meta}>{t('scorecard.selectRoundTitle')}</Text>
          <Pressable style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
            <Text style={styles.roundPickerText} numberOfLines={1}>
              {selectedRound?.name ?? selectedRound?.courseName ?? t('scorecard.selectRoundFallback')}
            </Text>
            <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
          </Pressable>
          <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
            <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
              <View style={styles.modalCard}>
                <FlatList
                  data={rounds}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => {
                    const isSelectable = isRoundSelectableByStatus(item.status);
                    const status = String(item.status ?? '-').trim() || '-';
                    return (
                      <Pressable
                        style={[
                          styles.roundOption,
                          item.id === roundId && styles.roundOptionActive,
                          !isSelectable ? { opacity: 0.5 } : null,
                        ]}
                        onPress={() => {
                          if (!isSelectable) {
                            return;
                          }

                          setRoundId(item.id);
                          setRoundPickerOpen(false);
                        }}
                        disabled={!isSelectable}
                      >
                        <Text style={styles.roundOptionTitle}>
                          {item.name ?? item.courseName ?? t('scorecard.selectRoundFallback')}
                        </Text>
                        <Text style={styles.roundOptionSubtitle}>
                          {isSelectable ? status : `${status} (Not started)`}
                        </Text>
                      </Pressable>
                    );
                  }}
                  ItemSeparatorComponent={() => <View style={styles.separator} />}
                />
              </View>
            </Pressable>
          </Modal>
        </>
      ) : null}
      {roundInfo?.name || roundInfo?.courseName ? (
        <Text style={styles.meta}>{roundInfo.name ?? roundInfo.courseName}</Text>
      ) : null}
      {roundInfo?.courseAddress ? <Text style={styles.meta}>{roundInfo.courseAddress}</Text> : null}
      {loading ? <ActivityIndicator color="#d5e6ff" /> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.table}>
        <View>
          <View style={styles.headerRow}>
            <Text style={[styles.cell, styles.holeCell]}>{t('scorecard.hole')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{t('scorecard.par')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{t('scorecard.index')}</Text>
            {players.map((player) => (
              <Text key={player.id} style={[styles.cell, styles.playerHeaderCell]} numberOfLines={1}>
                {player.name}
              </Text>
            ))}
          </View>
          <View style={styles.subHeaderRow}>
            <View style={styles.subHeaderLeftSpacer} />
            {players.map((player) => (
              <View key={`${player.id}-labels`} style={[styles.playerSubHeader, styles.playerSeparator]}>
                <Text style={[styles.cell, styles.scoreCell, styles.subHeaderText]}>{t('scorecard.strokes')}</Text>
                <Text style={[styles.cell, styles.scoreCell, styles.subHeaderText]}>{t('scorecard.points')}</Text>
              </View>
            ))}
          </View>
          {HOLES.map((hole) => {
            const meta = holeMeta.get(hole);
            return (
              <View key={`row-${hole}`} style={styles.row}>
                <Text style={[styles.cell, styles.holeCell]}>{hole}</Text>
                <Text style={[styles.cell, styles.metaCell]}>{meta?.par ?? '-'}</Text>
                <Text style={[styles.cell, styles.metaCell]}>{meta?.strokeIndex ?? '-'}</Text>
                {players.map((player) => (
                  <View key={`${player.id}-${hole}`} style={[styles.playerCells, styles.playerSeparator]}>
                    <View style={[styles.cell, styles.scoreCell, styles.scoreCellContainer]}>{renderStrokes(player, hole)}</View>
                    <Text style={[styles.cell, styles.scoreCell]}>{renderStableford(player.id, hole)}</Text>
                  </View>
                ))}
              </View>
            );
          })}
          <View style={[styles.row, styles.summaryRow]}>
            <Text style={[styles.cell, styles.holeCell]}>{t('scorecard.total')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>-</Text>
            <Text style={[styles.cell, styles.metaCell]}>-</Text>
            {players.map((player) => (
              <View key={`${player.id}-total`} style={[styles.playerCells, styles.playerSeparator]}>
                <Text style={[styles.cell, styles.scoreCell]}>{grossTotals.get(player.id) ?? '-'}</Text>
                <Text style={[styles.cell, styles.scoreCell]}>
                  {stablefordTotals.has(player.id) ? stablefordTotals.get(player.id) : '-'}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: '#ffffff',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0b0b0b',
  },
  meta: {
    color: '#4a4a4a',
  },
  error: {
    color: '#ff8c8c',
  },
  roundPicker: {
    borderWidth: 1,
    borderColor: '#d2d6dc',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundPickerText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
    flex: 1,
  },
  roundPickerHint: {
    fontSize: 12,
    color: '#1c4ed8',
    fontWeight: '700',
    marginLeft: 12,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    padding: 20,
    justifyContent: 'center',
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    maxHeight: '80%',
  },
  roundOption: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  roundOptionActive: {
    backgroundColor: '#eef2ff',
    borderRadius: 8,
  },
  roundOptionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
  },
  roundOptionSubtitle: {
    fontSize: 12,
    color: '#4a4a4a',
  },
  separator: {
    height: 1,
    backgroundColor: '#e5e7eb',
  },
  roundSelectRow: {
    borderWidth: 1,
    borderColor: '#d2d6dc',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
  },
  roundSelectName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
  },
  roundSelectMeta: {
    fontSize: 12,
    color: '#4a4a4a',
  },
  roundSelectLink: {
    fontSize: 12,
    color: '#1c4ed8',
    fontWeight: '700',
    marginTop: 4,
  },
  table: {
    paddingVertical: 6,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f3f6',
  },
  subHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fb',
  },
  subHeaderLeftSpacer: {
    width: COL_WIDTH * 3,
    borderLeftWidth: 1,
    borderLeftColor: '#d2d6dc',
    borderRightWidth: 2,
    borderRightColor: '#4b5563',
    backgroundColor: '#f8f9fb',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  summaryRow: {
    backgroundColor: '#f7f7f9',
  },
  cell: {
    width: COL_WIDTH,
    textAlign: 'center',
    color: '#121212',
    fontSize: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d2d6dc',
  },
  holeCell: {
    width: COL_WIDTH,
    fontWeight: '700',
  },
  metaCell: {
    width: COL_WIDTH,
  },
  playerHeaderCell: {
    width: COL_WIDTH * 2,
    fontWeight: '700',
  },
  playerSubHeader: {
    flexDirection: 'row',
  },
  playerCells: {
    flexDirection: 'row',
  },
  playerSeparator: {
    borderLeftWidth: 2,
    borderLeftColor: '#4b5563',
  },
  scoreCell: {
    width: COL_WIDTH,
  },
  scoreCellContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  strokeCell: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: COL_WIDTH,
    height: '100%',
  },
  strokeValue: {
    fontSize: 12,
    color: '#121212',
  },
  subHeaderText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#111827',
  },
  handicapText: {
    position: 'absolute',
    top: 0,
    right: 2,
    fontSize: 8,
    color: '#1f2937',
  },
});
